import { toast } from 'react-toastify';
import { formatDate } from '../../utils/formatUtils';

/**
 * Service d'exportation des notes d'évaluation avec en-tête institutionnel
 */

// Fonction pour exporter en CSV
export const exportNotesToCSV = (notes, controle, etablissement) => {
  const headers = ['Nom', 'Prénom', 'Email', 'Note', 'Mention', 'Observation', 'Saisi par', 'Date'];
  
  const csvContent = [
    headers.join(','),
    ...notes.map(note => [
      `"${note.etudiant.nom}"`,
      `"${note.etudiant.prenom}"`,
      `"${note.etudiant.email}"`,
      note.note,
      `"${note.mention}"`,
      `"${note.observation || ''}"`,
      `"${note.renseigner_par_username || ''}"`,
      `"${new Date(note.create_at).toLocaleDateString('fr-FR')}"`
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `notes-${controle?.libelle}-${controle?.numero}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success('Export CSV téléchargé');
};

// Fonction pour exporter en Excel
export const exportNotesToExcel = (notes, controle, etablissement) => {
  const headers = ['Nom', 'Prénom', 'Email', 'Note', 'Mention', 'Observation', 'Saisi par', 'Date'];
  
  const csvContent = [
    headers.join('\t'),
    ...notes.map(note => [
      note.etudiant.nom,
      note.etudiant.prenom,
      note.etudiant.email,
      note.note,
      note.mention,
      note.observation || '',
      note.renseigner_par_username || '',
      new Date(note.create_at).toLocaleDateString('fr-FR')
    ].join('\t'))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `notes-${controle?.libelle}-${controle?.numero}.xls`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success('Export Excel téléchargé');
};

// Fonction pour exporter en PDF avec l'en-tête institutionnel
export const exportNotesToPDF = (notes, controle, etablissement) => {
  // Debug des données d'établissement
  console.log('🏢 Données établissement pour PDF notes:', etablissement);
  console.log('🖼️ Logo URL:', etablissement?.logo_url);
  
  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Relevé de notes - ${controle?.libelle} N°${controle?.numero}</title>
        <meta charset="UTF-8">
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 1cm;
            padding: 0;
            color: black;
            background: white;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          
          th, td {
            border: 1px solid #333;
            padding: 6px 8px;
            text-align: left;
            font-size: 12px;
          }
          
          th {
            background-color: #f0f0f0;
            font-weight: bold;
          }
          
          .header {
            text-align: center;
            margin-bottom: 20px;
          }
          
          .header-container {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 15px;
          }
          
          .logo-container {
            width: 15%;
            min-height: 80px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .center-text {
            width: 70%;
            text-align: center;
          }
          
          .right-info {
            width: 15%;
            text-align: right;
          }
          
          .title-box {
            display: inline-block;
            border: 1px solid #000;
            padding: 5px 20px;
            margin-top: 15px;
          }
          
          .divider {
            width: 100px;
            height: 2px;
            background-color: black;
            margin: 3px auto;
          }
          
          .note-excellent { color: #10B981; font-weight: bold; }
          .note-bien { color: #3B82F6; font-weight: bold; }
          .note-ab { color: #F59E0B; font-weight: bold; }
          .note-passable { color: #F97316; font-weight: bold; }
          .note-insuffisant { color: #EF4444; font-weight: bold; }
          
          .document-date {
            text-align: center;
            margin-top: 30px;
            font-size: 12px;
            color: #666;
          }
          
          .logo-placeholder {
            width: 80px;
            height: 80px;
            border: 2px dashed #ccc;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            color: #666;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <!-- En-tête institutionnel -->
        <div class="header">
          <div class="header-container">
            <!-- Logo à gauche -->
            <div class="logo-container">
              ${etablissement?.logo_url ? `
                <img src="${etablissement.logo_url}" 
                    alt="Logo de l'établissement" 
                    style="max-height: 80px; max-width: 100%; object-fit: contain;"
                    onerror="console.error('Erreur chargement logo:', this.src); this.style.display='none'; this.nextElementSibling.style.display='flex';">
                <div class="logo-placeholder" style="display: none;">
                  LOGO<br>ÉTABLISSEMENT
                </div>
              ` : `
                <div class="logo-placeholder">
                  LOGO<br>ÉTABLISSEMENT
                </div>
              `}
            </div>
            
            <!-- Textes au centre -->
            <div class="center-text">
              <p style="font-weight: bold; text-transform: uppercase; margin: 0 0 2px 0; font-size: 14px;">
                RÉPUBLIQUE ${etablissement?.pays ? `DU ${etablissement.pays.toUpperCase()}` : ''}
              </p>
              <div class="divider"></div>
              ${etablissement?.ministere ? `
                <p style="text-transform: uppercase; margin: 2px 0; font-size: 12px;">
                  MINISTÈRE DE ${etablissement.ministere.toUpperCase()}
                </p>
              ` : ''}
              <h1 style="font-size: 18px; font-weight: bold; margin: 5px 0 2px 0;">
                ${etablissement?.libelle || etablissement?.nom || 'ÉTABLISSEMENT'}
              </h1>
              <p style="font-style: italic; margin: 2px 0; font-size: 14px;">
                ${etablissement?.devise || 'Excellence et Savoir'}
              </p>
              <div style="font-size: 12px; margin: 5px 0;">
                <span>Email: ${etablissement?.email || 'N/A'}</span>
                <span style="margin: 0 5px;">|</span>
                <span>Contact: ${etablissement?.contact || etablissement?.telephone || 'N/A'}</span>
                <span style="margin: 0 5px;">|</span>
                <span>BP: ${etablissement?.bp || 'N/A'}</span>
                ${(etablissement?.site_web || etablissement?.website) ? `
                  <span style="margin: 0 5px;">|</span>
                  <span>Site: ${etablissement.site_web || etablissement.website}</span>
                ` : ''}
              </div>
              <p style="font-size: 12px; margin: 2px 0;">
                ${etablissement?.adresse || 'Adresse non spécifiée'}
              </p>
            </div>
            
            <!-- Année académique à droite -->
            <div class="right-info">
              <div style="border: 1px solid #333; padding: 5px; display: inline-block; text-align: center;">
                <p style="margin: 0 0 2px 0; font-size: 12px;">Année Académique</p>
                <p style="margin: 0; font-weight: bold; font-size: 14px;">
                  ${controle?.annee?.libelle || 'N/A'}
                </p>
              </div>
            </div>
          </div>
          
          <!-- Titre du document -->
          <div style="text-align: center; margin-top: 10px;">
            <h2 class="title-box" style="font-size: 16px; font-weight: bold; letter-spacing: 1px; margin: 0;">
              RELEVÉ DE NOTES D'ÉVALUATION
            </h2>
            <h3 style="font-size: 16px; margin: 10px 0;">
              ${controle?.libelle} N°${controle?.numero}
            </h3>
          </div>
        </div>
        
        <!-- Informations de l'évaluation -->
        <table style="margin-bottom: 20px;">
          <tr>
            <th>Module</th>
            <th>Classe</th>
            <th>Date d'évaluation</th>
            <th>Type</th>
            <th>Coefficient</th>
          </tr>
          <tr>
            <td>${controle?.module?.module?.libelle || 'Non défini'}</td>
            <td>${controle?.classe?.libelle || 'Non spécifiée'}</td>
            <td>${formatDate(controle?.date_controle) || 'Non définie'}</td>
            <td>${controle?.type_controle || 'Évaluation'}</td>
            <td>${controle?.coefficient || '1'}</td>
          </tr>
        </table>
        
        <!-- Tableau des notes -->
        <table>
          <thead>
            <tr>
              <th style="width: 20%;">Nom</th>
              <th style="width: 20%;">Prénom</th>
              <th style="width: 10%;">Note (/20)</th>
              <th style="width: 15%;">Mention</th>
              <th style="width: 35%;">Observation</th>
            </tr>
          </thead>
          <tbody>
            ${notes.map(note => {
              let noteClass = '';
              if (note.note >= 16) noteClass = 'note-excellent';
              else if (note.note >= 14) noteClass = 'note-bien';
              else if (note.note >= 12) noteClass = 'note-ab';
              else if (note.note >= 10) noteClass = 'note-passable';
              else noteClass = 'note-insuffisant';
              
              return `
                <tr>
                  <td>${note.etudiant.nom}</td>
                  <td>${note.etudiant.prenom}</td>
                  <td class="${noteClass}">${note.note}/20</td>
                  <td>${note.mention}</td>
                  <td>${note.observation || '-'}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <!-- Statistiques rapides -->
        <div style="margin-top: 20px; display: flex; justify-content: space-around; border: 1px solid #333; padding: 10px;">
          <div style="text-align: center;">
            <strong>Total étudiants:</strong><br>${notes.length}
          </div>
          <div style="text-align: center;">
            <strong>Moyenne générale:</strong><br>${notes.length > 0 ? Math.round((notes.reduce((sum, note) => sum + parseFloat(note.note), 0) / notes.length) * 100) / 100 : 0}/20
          </div>
          <div style="text-align: center;">
            <strong>Admis:</strong><br>${notes.filter(n => n.note >= 10).length}
          </div>
          <div style="text-align: center;">
            <strong>Taux de réussite:</strong><br>${notes.length > 0 ? Math.round((notes.filter(n => n.note >= 10).length / notes.length) * 100) : 0}%
          </div>
        </div>
        
        <!-- Date du document -->
        <div class="document-date">
          <p>Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open('', '_blank', 'width=800,height=600');
  printWindow.document.write(printContent);
  printWindow.document.close();
  
  // Ajouter un délai plus long pour permettre le chargement des images
  setTimeout(() => {
    try {
      printWindow.focus();
      printWindow.print();
      console.log("✅ Impression notes d'évaluation lancée avec succès");
    } catch (error) {
      console.error("❌ Erreur lors de l'impression:", error);
      toast.error("Erreur lors de l'impression. Veuillez réessayer.");
    }
  }, 2000); // Délai augmenté pour le chargement des images
}; 